import {
    Widget,
    blockUIFactory,
    YandexSingleChatConfig,
    yandexUnreadCounterFactory,
} from '@yandex-int/messenger.widget';
import b from 'b_';
import { Close } from 'education-icons';
import { Text, KeyboardContext } from 'platform-components';
import { KEYBOARD_TYPES } from 'platform-components/constants';
import React, { useEffect, useMemo, useRef } from 'react';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import { connect } from 'react-redux';
import { useMount } from 'react-use';
import { bindActionCreators, Dispatch, Action } from 'redux';

import * as lessonViewSelectors from 'common.components/lesson-view.store/selectors';
import { getSubjectFromPath } from 'utils/path';

import * as lessonWidgetSelectors from '../../bundles/lab/components/lesson-editor-widget/selectors';

import ChatWidgetButton from './__button/chat-widget__button';
import { actions } from './actions';
import * as selectors from './selectors';

import '@yandex-int/messenger.widget/lib/ui/popup.css';
import './chat-widget.scss';

type StoreProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
interface HOCProps extends WrappedComponentProps {}

interface Props extends StoreProps, DispatchProps, HOCProps {}

const ui = blockUIFactory();
const unreadCounter = yandexUnreadCounterFactory();
const getWidget = () => {
    if (isSSR) {
        return null;
    }

    return new Widget(
        new YandexSingleChatConfig({
            serviceId: 35,
            iframeOpenData: {
                guid: '2e20c206-c60f-9eba-6f4f-dce537b1a747',
            },
            flags: {
                recommended_chats: 0,
                disableNavigation: 1,
                disableChatHeader: 1,
                notification_suggest: 0,
                onboarding: 0,
                hideClose: 1,
            },
        })
    )
        .addPlugin(unreadCounter)
        .setUI(ui);
};

export const ChatWidget = ({
    isLessonWidgetVisible,
    isChatWidgetVisible,
    isLessonModalVisible,
    toggleWidget,
    unreadCount,
    setUnreadCount,
}: Props) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const widget = useMemo(getWidget, []);

    useMount(() => {
        if (!isSSR) {
            widget?.init();
            ui.mount(contentRef.current!);
            unreadCounter.onChanged.addListener((data) => {
                if (data.value !== undefined) {
                    setUnreadCount(data.value);
                }
            });
        }
    });

    useEffect(
        function hideOnLessonModalOpening() {
            // сворачиваем виджет при открытии виджета урока
            if (isLessonWidgetVisible) {
                toggleWidget(false);
            }
        },
        [isLessonWidgetVisible, toggleWidget]
    );

    useEffect(
        function toggleWidgetState() {
            if (isChatWidgetVisible) {
                widget?.show();
            }
        },
        [isChatWidgetVisible, widget]
    );

    return (
        <>
            <KeyboardContext.Consumer>
                {(context) => {
                    const shouldShowKeyboard = context[KEYBOARD_TYPES.PRIMARY].shouldShowKeyboard;

                    if (!shouldShowKeyboard && !isChatWidgetVisible) {
                        return (
                            <ChatWidgetButton
                                hasUnreadMessage={unreadCount > 0}
                                isHidden={isLessonModalVisible}
                                onClick={() => toggleWidget(true)}
                            />
                        );
                    }

                    return null;
                }}
            </KeyboardContext.Consumer>
            <div className={b('chat-widget', 'wrapper', { hidden: !isChatWidgetVisible })}>
                <div className="chat-widget__header">
                    <Text id="chatWidget.title.support" weight={Text.WEIGHT.M} />
                    <Close className="chat-widget__close" onClick={() => toggleWidget(false)} />
                </div>
                <div className="chat-widget__content" ref={contentRef} />
            </div>
        </>
    );
};

const mapStateToProps = (storeState: any) => {
    return {
        isChatWidgetVisible: selectors.getIsChatWidgetVisible(storeState),
        isLessonWidgetVisible: lessonWidgetSelectors.getShouldShowWidget(storeState, { subject: getSubjectFromPath() }),
        isLessonModalVisible: lessonViewSelectors.getShouldShowModal(storeState),
        unreadCount: selectors.getUnreadCount(storeState),
    };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>) => {
    return bindActionCreators(
        {
            toggleWidget: actions.toggleWidget,
            setUnreadCount: actions.setUnreadCount,
        },
        dispatch
    );
};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(ChatWidget));
